/**
 * Image detail page with verifications
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { VerificationCard } from '../components/VerificationCard';
import { Loading } from '../components/Loading';
import { mediaApi, verificationApi } from '../services/api';
import type { Media } from '../../../../packages/shared/src/types';
import type { VerificationDetail } from '../types';

export function ImageDetail() {
  const { id } = useParams<{ id: string }>();
  const [image, setImage] = useState<Media | null>(null);
  const [verifications, setVerifications] = useState<VerificationDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const [imageRes, verificationsRes] = await Promise.all([
        mediaApi.get(id),
        verificationApi.getByMedia(id, page, 10),
      ]);

      if (imageRes.success && imageRes.data) {
        setImage(imageRes.data);
      } else {
        setError(imageRes.error?.message || 'Failed to load image');
      }

      if (verificationsRes.success && verificationsRes.data) {
        setVerifications(verificationsRes.data.items);
        setTotalPages(verificationsRes.data.totalPages);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    }

    setIsLoading(false);
  }, [id, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyUrl = useCallback(() => {
    if (image?.url) {
      const fullUrl = window.location.origin + image.url;
      navigator.clipboard.writeText(fullUrl);
      setToast({ message: 'URL copied to clipboard!', type: 'success' });
    }
  }, [image]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    const response = await mediaApi.delete(id);
    if (response.success) {
      setToast({ message: 'Image deleted successfully!', type: 'success' });
      setTimeout(() => {
        window.location.href = '/images';
      }, 1000);
    } else {
      setToast({ message: response.error?.message || 'Delete failed', type: 'error' });
    }
  }, [id]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loading size="lg" text="Loading image details..." />
        </div>
      </Layout>
    );
  }

  if (error || !image) {
    return (
      <Layout>
        <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg mb-6">
          <p className="text-red-300">{error || 'Image not found'}</p>
        </div>
        <Link
          to="/images"
          className="text-indigo-400 hover:text-indigo-300 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Images
        </Link>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Link */}
        <Link
          to="/images"
          className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Images
        </Link>

        {/* Toast */}
        {toast && (
          <div className={`p-4 rounded-lg border ${
            toast.type === 'success' 
              ? 'bg-green-900/50 border-green-700' 
              : 'bg-red-900/50 border-red-700'
          }`}>
            <p className={toast.type === 'success' ? 'text-green-300' : 'text-red-300'}>
              {toast.message}
            </p>
          </div>
        )}

        {/* Image Details */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Preview */}
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
              <img
                src={image.url}
                alt={image.originalName}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold text-white mb-2">{image.originalName}</h2>
                <span className={`px-3 py-1 text-sm font-medium rounded ${
                  image.status === 'active' ? 'bg-green-900/80 text-green-300' :
                  image.status === 'archived' ? 'bg-yellow-900/80 text-yellow-300' :
                  'bg-red-900/80 text-red-300'
                }`}>
                  {image.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">File Name</p>
                  <p className="text-sm text-white mt-1 font-mono">{image.filename}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">File Size</p>
                  <p className="text-sm text-white mt-1">{formatFileSize(image.size)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Mime Type</p>
                  <p className="text-sm text-white mt-1">{image.mimeType}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Created</p>
                  <p className="text-sm text-white mt-1">{formatDate(image.createdAt)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={handleCopyUrl}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Share URL
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Image
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Verifications */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Verification Attempts ({verifications.length})
          </h3>

          {verifications.length > 0 ? (
            <>
              <div className="space-y-4">
                {verifications.map((verification) => (
                  <VerificationCard
                    key={verification.id}
                    verification={verification as any}
                    mediaName={image.originalName}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-gray-400 px-4">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-400 text-center py-8">
              No verification attempts for this image yet
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default ImageDetail;
